import type { ChangeEvent } from 'react'
import { AlertCircle } from 'lucide-react'
import type { SendOtp, VerifyOtp } from '@/modules/auth/use-cases'
import { Button, Input, SeparatedPhoneInput } from '@/shared/ui/primitives'
import { cn } from '@/shared/ui/cn'
import { OtpVerificationStep } from '@/modules/auth/ui/components'
import { formatTryAgainLabel } from '@/shared/ui/hooks'
import { useAuthStep } from './use-auth-step'

export type AuthStepProps = {
  onSendOtp: SendOtp
  onVerifyOtp: VerifyOtp
  onVerified: () => void
}

export function AuthStep({ onSendOtp, onVerifyOtp, onVerified }: AuthStepProps) {
  const vm = useAuthStep({ onSendOtp })

  if (vm.subStep === 'otp') {
    return (
      <OtpVerificationStep
        variant="inline"
        identifier={vm.identifier}
        channel={vm.currentMode}
        onSendOtp={onSendOtp}
        onVerifyOtp={onVerifyOtp}
        onVerifySuccess={async () => {
          await vm.handleVerifySuccess()
          onVerified()
        }}
        onBack={vm.backToIdentifier}
      />
    )
  }

  return (
    <section data-testid="step-auth" className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-3xl font-bold leading-tight text-foreground">Create Your Account</h1>
        <p className="text-sm text-muted-foreground">Or log in with an existing account</p>
      </div>

      <form onSubmit={vm.handleSubmit} className="flex flex-col gap-4" noValidate>
        {vm.isPhoneLoginEnabled ? (
          <div
            role="tablist"
            aria-label="Auth method"
            className="grid grid-cols-2 gap-1 rounded-md bg-muted p-1 text-sm"
          >
            <button
              type="button"
              role="tab"
              aria-selected={vm.currentMode === 'email'}
              onClick={() => vm.setMode('email')}
              className={cn(
                'rounded-sm px-3 py-1.5 font-medium transition-colors',
                vm.currentMode === 'email'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              Email
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={vm.currentMode === 'phone'}
              onClick={() => vm.setMode('phone')}
              className={cn(
                'rounded-sm px-3 py-1.5 font-medium transition-colors',
                vm.currentMode === 'phone'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              Phone
            </button>
          </div>
        ) : null}

        {vm.currentMode === 'phone' ? (
          <div className="flex flex-col gap-1">
            <SeparatedPhoneInput
              id="auth-step-phone"
              aria-label="Phone"
              value={vm.phoneE164}
              onChange={vm.setPhone}
              onBlur={vm.handleBlur}
              autoFocus
              placeholder="Phone"
              autoComplete="tel"
            />
            {vm.fieldError ? (
              <p data-testid="auth-step-field-error" className="text-xs text-destructive">
                {vm.fieldError}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <Input
              id="auth-step-email"
              type="email"
              aria-label="Email"
              value={vm.email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => vm.setEmail(e.target.value)}
              onBlur={vm.handleBlur}
              placeholder="Email"
              autoComplete="email"
              autoFocus
            />
            {vm.fieldError ? (
              <p data-testid="auth-step-field-error" className="text-xs text-destructive">
                {vm.fieldError}
              </p>
            ) : null}
          </div>
        )}

        {vm.errorMessage ? (
          <div
            role="alert"
            data-testid="auth-step-error"
            className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
          >
            <AlertCircle aria-hidden className="size-4 shrink-0 mt-0.5" />
            <span>{vm.errorMessage}</span>
          </div>
        ) : null}

        <Button
          type="submit"
          disabled={vm.isSubmitting || vm.isCooldownActive}
          className="h-12 w-full mt-2"
        >
          {vm.isSubmitting
            ? 'Sending…'
            : vm.isCooldownActive
              ? formatTryAgainLabel(vm.cooldownSeconds)
              : 'Continue'}
        </Button>

        <p className="text-center text-xs text-muted-foreground">
          By continuing, you agree to receive a one-time verification code.
        </p>
      </form>
    </section>
  )
}
